import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Verified from "../../resources/icons/shield-tick.svg";
import EntityConstants from "../../configs/EntityConstants";
import { GET, POST } from "../../service/CRUDService";
import Revoked from "../../resources/icons/white_shiel.svg";
import CfLoader from "../../common/CfLoader/CfLoader";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import toast from "react-hot-toast";
import AuthContext from "../../store/auth-context";
import useInterceptedNavigate from "../../hooks/useInterceptedNavigate";
import Toast from "../../common/Toaster/Toaster";
import DownloadIcon from "../../resources/icons/download-white-icon.svg";
import { DocumentPreview } from "../../common/DocumentPreview/DocumentPreview";
import { formatLocalDateTime } from "../../utils/formatLocalDateTime";

const ViewCredential = () => {
  const { credential_id } = useParams();
  const [credential, setCredential] = useState({});
  const [verificationLogId, setVerificationLogId] = useState("");
  const [isCertLoaded, setIsCertLoaded] = useState(false);
  const [isLoadedQrCode, setIsLoadedQrCode] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [uploadedDocument, setUploadedDocument] = useState("");
  const svgContainerRef = useRef(null);
  const authContext = useContext(AuthContext);

  const navigate = useInterceptedNavigate();
  const defaultNavigate = useNavigate();

  const [text, setText] = useState("");
  const maxChars = 200;
  const location = useLocation();

  const { transaction_comments, pdf_url, name, transaction_id, issuer } =
    location.state || {};

  const showDownloadButton = pdf_url || pdfUrl;

  const downloadPDF = async (url) => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to fetch the file");
      }

      // Extract the filename from the Content-Disposition header
      const disposition = response.headers.get("Content-Disposition");
      let filename = `${credential_id}.pdf`; // Default filename

      if (disposition && disposition.includes("attachment")) {
        const matches = /filename="([^"]*)"/.exec(disposition);
        if (matches != null && matches[1]) {
          filename = matches[1];
        }
      }

      // Convert the response to a blob
      const blob = await response.blob();

      // Create a URL for the blob
      const blobUrl = window.URL.createObjectURL(blob);

      // Create a temporary link element
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename; // Use the extracted filename
      document.body.appendChild(link);

      // Programmatically click the link to trigger the download
      link.click();

      // Remove the link from the DOM
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading the file:", error);
    }
  };

  const handleChange = (e) => {
    if (e.target.value.length <= maxChars) {
      setText(e.target.value);
    }
    authContext.isFormDataChangedHandler(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitComment(text); // Call the submitComment function
    setText(""); // Reset the textarea after submission
  };

  const submitComment = (comment) => {
    if (comment) {
      const payload = {
        transaction_id: verificationLogId,
        comments: comment,
      };
      POST(EntityConstants.UPDATE_TRANSACTION_COMMENT, payload)
        .then((res) =>
          res.status === 204
            ? defaultNavigate("/verifier/transaction/all")
            : toast.error(
                "Error occured while updating comments.\nPlease try again."
              )
        )
        .catch((err) => console.log("error >>> ", err));
    } else {
      defaultNavigate("/verifier/transaction/all");
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (authContext.isFormDataChanged) {
        authContext.isConfirmationModalOpenHandler(true); // Open modal
        event.preventDefault();
        return "";
      }
    };

    const handlePopState = (event) => {
      if (authContext.isFormDataChanged) {
        authContext.pendingNavigationHandler(event); // Store the event
        authContext.isConfirmationModalOpenHandler(true); // Open modal
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [authContext.isFormDataChanged]);

  const handleConfirmLeave = () => {
    authContext.isConfirmationModalOpenHandler(false);
    authContext.isFormDataChangedHandler(false); // Reset the form change status
    if (typeof authContext.pendingNavigation === "object") {
      navigate(-1);
    } else {
      navigate(authContext.pendingNavigation);
    }
  };

  const handleCancelLeave = () => {
    authContext.isConfirmationModalOpenHandler(false);
    if (authContext.pendingNavigation) {
      window.history.pushState(null, null, window.location.pathname); // Cancel navigation
    }
  };

  useEffect(() => {
    window.history.pushState(null, null, window.location.pathname);
  }, []);

  const getSvgTopLeftPosition = () => {
    const container = svgContainerRef.current;
    if (container) {
      const svg = container.querySelector("svg");
      if (svg) {
        const svgRect = svg.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        return {
          top: svgRect.top - containerRect.top,
          left: svgRect.left - containerRect.left,
        };
      }
    }
    return { top: 0, left: 0 };
  };

  const handleSvgLoad = (url) => {
    fetch(url)
      .then((res) => res.text())
      .then((svg) => {
        const svgContainer = svgContainerRef.current;
        if (svgContainer) {
          svgContainer.style.filter =
            "drop-shadow(0 0 2px rgba(0, 0, 0, 0.264))";
          svgContainer.innerHTML = svg;
          setIsCertLoaded(true);
        }
      });
  };

  const verify = (credential_id) => {
    POST(EntityConstants.VERIFY_CREDENTIAL, { encoded_data: credential_id })
      .then((res) => {
        return res.json().then((data) => ({ res, data })); // Ensure 'data' is resolved
      })
      .then((ResData) => {
        if (ResData.res.ok) {
          setCredential(ResData.data.verification_details);
          setVerificationLogId(ResData.data.verification_log_id);
          handleSvgLoad(ResData.data.verification_details?.svg_string);
          setUploadedDocument(ResData.data.uploaded_document);
          setPdfUrl(ResData.data.verification_details.pdf_url);
          setText(ResData.data.comments || "");
          authContext.isFormDataChangedHandler(true);
          if (
            ResData.data.payment_status === "Done" &&
            ResData.data.is_payment_required === false &&
            localStorage.getItem("payment_status") === "success"
          ) {
            Toast({
              type: "success",
              title: "Payment Success",
              message: "Your payment was successful",
            });
            localStorage.removeItem("payment_status");
          }
        } else if (ResData.res.status === 400) {
          toast.error(ResData.data.verification_details.message);
          navigate("/");
        }
      })
      .catch((err) => {
        console.log("error >> ", err);
      });
  };

  useEffect(() => {
    verify(credential_id);
  }, [credential_id]);

  return (
    <div className="flex flex-col lg:flex-row w-full pt-20 min-h-screen max-h-screen">
      <section className="flex-1 lg:flex-[7] bg-inherit flex justify-center items-center p-4 lg:p-0">
        <div className="flex justify-center items-center w-full h-full">
          <DocumentPreview documentUrl={uploadedDocument} />
        </div>
      </section>
      <section
        id="details_container"
        className="flex-1 lg:flex-[3] md:min-h-fit bg-cfSecondary p-4 flex flex-col items-center lg:items-start lg:justify-center"
      >
        <img
          src={credential.qr_code}
          onLoad={() => setIsLoadedQrCode(true)}
          alt=""
          className="mb-4  w-11/12 min-w-[5rem] max-w-sm self-center"
        />
        {!isLoadedQrCode && (
          <div className="w-full min-h-fit min-w-fit flex justify-center items-center my-5">
            <CfLoader />
          </div>
        )}
        <div className="text-center lg:text-left flex flex-col items-center self-center">
          <h2 className="text-lg lg:text-xl font-semibold">
            {credential.credential_id}
          </h2>
          <p className="text-sm lg:text-base">Reference ID on CredIssuer</p>
          <span
            className={`flex items-center justify-center mt-2 ${
              credential.status === "Verified" && !credential.is_revoked
                ? "text-cfPrimary border-2 border-cfPrimary"
                : (credential.is_revoked || credential.status === "Rejected") &&
                  "text-cfSecondary border-red-500 bg-red-500"
            } w-full py-2 px-4 rounded-full`}
          >
            {credential.status && (
              <img
                src={
                  credential.status === "Verified" && !credential.is_revoked
                    ? Verified
                    : credential.is_revoked ||
                      credential.status === "Rejected" ||
                      credential.is_expired === "Expired"
                    ? Revoked
                    : null
                }
                alt=""
                className="mr-2 w-6 h-6"
              />
            )}
            <p className="text-sm lg:text-base">
              {credential.status === "Verified" && !credential.is_revoked
                ? "Credential Verified"
                : credential.is_revoked && "Verified, but Revoked"
                ? "Verified, but Revoked"
                : credential.status === "Rejected"
                ? "Verified, but Rejected"
                : credential.is_expired
                ? "Credential Expired"
                : "Loading..."}
            </p>
          </span>
          <div>
            <span className="flex items-center mt-4">
              <p className="text-sm lg:text-base">Issued By:&nbsp;</p>
              <p className="text-sm lg:text-base font-semibold">
                {credential.issued_by}
              </p>
            </span>
            <span className="flex items-center mt-2">
              <p className="text-sm lg:text-base">Issued On:&nbsp;</p>
              <p className="text-sm lg:text-base font-semibold">
                {formatLocalDateTime(credential.issued_at_epoch)}
              </p>
            </span>
            <span className="flex items-center mt-2">
              <p className="text-sm lg:text-base">Expires On:&nbsp;</p>
              <p className="text-sm lg:text-base font-semibold">
                {formatLocalDateTime(credential.expires_on_epoch)}
              </p>
            </span>
          </div>
        </div>
        <form
          onSubmit={handleSubmit}
          className="mt-4 w-11/12 min-w-[5rem] max-w-sm self-center"
        >
          <div className="mb-2">
            <textarea
              id="view-credential-comments-textarea"
              value={text || transaction_comments}
              onChange={handleChange}
              className="w-full h-32 p-2 border border-gray-300 rounded-md resize-none overflow-y-auto"
              placeholder="Enter your comment..."
            />
          </div>
          <div className="text-right text-sm mb-2 text">
            <span>{maxChars - text.length} characters left</span>
          </div>
          <div className={`flex mb-2 ${showDownloadButton ? "mb-2" : "mb-28"}`}>
            <button
              id="view-credential-submit-button"
              type="submit"
              className="text-lg w-full font-semibold text-cfSecondary focus:outline-none bg-cfPrimaryButton hover:bg-cfPrimaryButtonHover py-2.5 px-4 rounded-xl"
            >
              Submit
            </button>
          </div>
          {showDownloadButton && (
            <div className="flex mb-28">
              <button
                id="view-credential-download-report-button"
                onClick={(e) => {
                  e.stopPropagation();
                  downloadPDF(pdf_url || pdfUrl);
                }}
                type="button"
                className="text-lg w-full font-semibold text-cfSecondary focus:outline-none bg-cfPrimaryButton hover:bg-cfPrimaryButtonHover py-2.5 px-4 rounded-xl"
              >
                <div className="flex gap-2 justify-center">
                  <img src={DownloadIcon} alt="" />
                  <div>Download Verification Report</div>
                </div>
              </button>
            </div>
          )}
        </form>
      </section>
    </div>
  );
};

export default ViewCredential;
